<template>
  <div class="project-settings-page">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link to="/projects" class="breadcrumb-item">
        Projects
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link :to="`/projects/${projectId}`" class="breadcrumb-item">
        {{ projectName || 'Project Hub' }}
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">Project Settings</span>
    </div>

    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="fas fa-cogs"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">Project Settings</h1>
          <p class="page-subtitle">
            Configure project-specific settings and compliance check preferences for {{ projectName || 'this project' }}.
          </p>
        </div>
      </div>
      <div class="header-actions">
        <router-link :to="`/projects/${projectId}`" class="btn btn-outline-primary">
          <i class="fas fa-arrow-left"></i> Back to Project Hub
        </router-link>
      </div>
    </div>

    <!-- Settings Content -->
    <div class="settings-content">
      
      <!-- Terminology Settings -->
      <div class="settings-section">
        <div class="section-header">
          <div class="header-icon">
            <i class="fas fa-language"></i>
          </div>
          <div class="header-text">
            <h3>Project Terminology</h3>
            <p>Customize how we refer to companies and locations in this project</p>
          </div>
        </div>
        
        <div class="terminology-form">
          <div class="form-row">
            <div class="form-group">
              <label for="entity-terminology">
                <i class="fas fa-building"></i>
                What do you call them? *
              </label>
              <select 
                id="entity-terminology" 
                v-model="terminologySettings.entity_terminology"
                @change="onEntityTermChange"
                class="form-control">
                <option value="companies">Companies</option>
                <option value="subcontractors">Subcontractors</option>
                <option value="vendors">Vendors</option>
                <option value="franchisees">Franchisees</option>
                <option value="law_firms">Law Firms</option>
                <option value="suppliers">Suppliers</option>
                <option value="partners">Partners</option>
                <option value="patients">Patients</option>
                <option value="custom">Custom...</option>
              </select>
              <span class="help-text">Choose what to call the entities in this project</span>
            </div>
            
            <div class="form-group">
              <label for="location-terminology">
                <i class="fas fa-map-marker-alt"></i>
                What do you call their locations? *
              </label>
              <select 
                id="location-terminology" 
                v-model="terminologySettings.location_terminology"
                @change="onLocationTermChange"
                class="form-control">
                <option value="locations">Locations</option>
                <option value="stores">Stores</option>
                <option value="sites">Sites</option>
                <option value="jobsites">Job sites</option>
                <option value="houses">Houses</option>
                <option value="buildings">Buildings</option>
                <option value="offices">Offices</option>
                <option value="centers">Centers</option>
                <option value="branches">Branches</option>
                <option value="custom">Custom...</option>
              </select>
              <span class="help-text">Choose what to call the locations</span>
            </div>
          </div>
          
          <!-- Custom Entity Terms -->
          <div v-if="terminologySettings.entity_terminology === 'custom'" class="form-row">
            <div class="form-group">
              <label for="entity-singular">Custom Term (Singular) *</label>
              <input 
                id="entity-singular"
                v-model="terminologySettings.entity_term_singular"
                class="form-control"
                placeholder="e.g., Franchisee">
            </div>
            <div class="form-group">
              <label for="entity-plural">Custom Term (Plural) *</label>
              <input 
                id="entity-plural"
                v-model="terminologySettings.entity_term_plural"
                class="form-control"
                placeholder="e.g., Franchisees">
            </div>
          </div>
          
          <!-- Custom Location Term -->
          <div v-if="terminologySettings.location_terminology === 'custom'" class="form-row">
            <div class="form-group">
              <label for="location-custom">Custom Location Term *</label>
              <input 
                id="location-custom"
                v-model="terminologySettings.location_term_custom"
                class="form-control"
                placeholder="e.g., Facility">
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="location-match-strategy">
                <i class="fas fa-link"></i>
                How should certificates attach to existing records?
              </label>
              <select
                id="location-match-strategy"
                v-model="terminologySettings.location_match_strategy"
                class="form-control">
                <option value="">Automatic (recommended)</option>
                <option value="entity_name">Named insured / company name</option>
                <option value="store_code">Store or location code</option>
                <option value="address">Site name or street address</option>
              </select>
              <span class="help-text">
                Automatic keeps franchise/store projects code-based and uses the named insured for other projects.
              </span>
            </div>
          </div>
          
          <!-- Preview -->
          <div class="terminology-preview">
            <h4><i class="fas fa-eye"></i> Preview</h4>
            <div class="preview-examples">
              <p><strong>UI will display:</strong></p>
              <ul>
                <li>"{{ previewEntityPlural }}" instead of "Entities"</li>
                <li>"Add {{ previewEntitySingular }}" button</li>
                <li>"{{ previewLocationPlural }}" instead of "Locations"</li>
              </ul>
            </div>
          </div>
          
          <button 
            @click="saveTerminology" 
            class="btn btn-primary"
            :disabled="savingTerminology || !isTerminologyValid">
            <i class="fas fa-save"></i>
            {{ savingTerminology ? 'Saving...' : 'Save Terminology' }}
          </button>
        </div>
      </div>
      
      <!-- Notification Settings (PR2) -->
      <div class="settings-section">
        <div class="section-header">
          <div class="header-icon">
            <i class="fas fa-bell"></i>
          </div>
          <div class="header-text">
            <h3>Email Notifications</h3>
            <p>Control when gap-notification emails are sent automatically</p>
          </div>
        </div>

        <div class="notification-form">
          <div class="form-group toggle-group">
            <label class="toggle-label">
              <input
                type="checkbox"
                v-model="notificationSettings.notify_on_upload_completion"
                :disabled="savingNotifications"
              >
              <span class="toggle-slider"></span>
              <span class="toggle-text">
                <strong>Auto-send gap email after secure-link uploads</strong>
                <small>
                  When ON, the resolved agent contact is emailed automatically the moment a
                  comprehensive check completes with failures. When OFF (default), the email
                  must be sent manually from the Analysis Results screen.
                </small>
              </span>
            </label>
          </div>

          <div class="notification-help">
            <i class="fas fa-info-circle"></i>
            Internal/staff uploads <strong>never</strong> auto-fire the email regardless of
            this setting. This toggle only affects uploads that come in through a broker's
            secure link.
          </div>

          <button
            @click="saveNotificationSettings"
            class="btn btn-primary"
            :disabled="savingNotifications">
            <i class="fas fa-save"></i>
            {{ savingNotifications ? 'Saving...' : 'Save Notification Settings' }}
          </button>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-header">
          <div class="header-icon">
            <i class="fas fa-envelope"></i>
          </div>
          <div class="header-text">
            <h3>Status email</h3>
            <p>
              A recurring note for the people supporting this project's compliance book:
              how many {{ previewEntityPlural.toLowerCase() }} are active, how the check is landing,
              and whether last week's gap emails were delivered.
            </p>
          </div>
        </div>

        <div class="notification-form">
          <div class="form-group toggle-group">
            <label class="toggle-label">
              <input
                type="checkbox"
                v-model="statusReport.enabled"
                :disabled="savingStatusReport"
              >
              <span class="toggle-text">
                <strong>Send a status email on a schedule</strong>
                <small>
                  Off until you add addresses. The morning send uses the addresses below
                  and does not go to franchisees.
                </small>
              </span>
            </label>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">How often</label>
              <select v-model="statusReport.interval" class="form-control" :disabled="savingStatusReport">
                <option value="weekly">Weekly</option>
                <option value="daily">Every morning</option>
              </select>
            </div>
            <div v-if="statusReport.interval === 'weekly'" class="form-group">
              <label class="form-label">Which day</label>
              <select v-model.number="statusReport.weekday" class="form-control" :disabled="savingStatusReport">
                <option v-for="day in weekdays" :key="day.value" :value="day.value">{{ day.label }}</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Delivery addresses</label>
            <textarea
              v-model="statusReport.recipientText"
              class="form-control"
              rows="3"
              placeholder="one address per line"
              :disabled="savingStatusReport"
            ></textarea>
            <span class="help-text">
              These addresses receive the scheduled email and Send now. The mail covers this project only.
            </span>
          </div>

          <p v-if="statusReport.lastSentAt" class="help-text">
            Last sent {{ statusReportLastSentLabel }}.
          </p>
          <p v-if="statusReportError" class="status-report-error">{{ statusReportError }}</p>
          <p v-if="statusReportMessage" class="status-report-ok">{{ statusReportMessage }}</p>

          <div class="status-report-actions">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="savingStatusReport"
              @click="saveStatusReport"
            >
              {{ savingStatusReport ? 'Saving...' : 'Save status email' }}
            </button>
            <button
              type="button"
              class="btn btn-outline-primary"
              :disabled="sendingStatusNow"
              @click="sendStatusReportNow"
            >
              {{ sendingStatusNow ? 'Sending...' : 'Send now to the delivery list' }}
            </button>
          </div>

          <div class="status-report-test">
            <button
              v-if="!statusReport.showTest"
              type="button"
              class="btn btn-outline-primary"
              @click="statusReport.showTest = true"
            >
              Send a test
            </button>
            <div v-else>
              <label class="form-label" for="status-report-test-email">Send this test to</label>
              <p class="help-text">
                One address. This test does not use the delivery list and does not turn the schedule on.
              </p>
              <div class="status-report-test-row">
                <input
                  id="status-report-test-email"
                  v-model="statusReport.testEmail"
                  type="email"
                  class="form-control"
                  placeholder="name@example.com"
                  autocomplete="off"
                  :disabled="sendingStatusPreview"
                >
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  :disabled="sendingStatusPreview"
                  @click="previewStatusReport"
                >
                  {{ sendingStatusPreview ? 'Sending test...' : 'Send test' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- COI Upload Request Email Configuration (PR B of May-2026 COI audit) -->
      <!-- Centralized here so admins can edit the certificate-holder block,
           project contact, and email template override from one place. The
           Send Upload Request modal now just *reads* this and links back. -->
      <div class="settings-section">
        <div class="section-header">
          <div class="header-icon">
            <i class="fas fa-envelope-open-text"></i>
          </div>
          <div class="header-text">
            <h3>COI Upload Request Email</h3>
            <p>
              Certificate holder, project contact, and email template applied to every
              document-upload-request email sent for this project. Configure once and it
              flows into every send (basic, AI-drafted, and gap follow-up).
            </p>
          </div>
        </div>

        <div class="coi-config-form">
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-building"></i>
              Certificate Holder Name
            </label>
            <input
              v-model="coiConfig.certificate_holder_name"
              type="text"
              class="form-control"
              placeholder="e.g. Viera Builders, Inc and The Viera Company"
              :disabled="savingCoiConfig"
            >
            <span class="help-text">
              Appears in the <code>CERTIFICATE HOLDER:</code> block of the email body.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-map-marker-alt"></i>
              Certificate Holder Address
            </label>
            <textarea
              v-model="coiConfig.certificate_holder_address"
              class="form-control"
              rows="3"
              placeholder="7380 Murrell Road, Suite 202&#10;Viera, FL 32940"
              :disabled="savingCoiConfig"
            ></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-user"></i>
                Project Contact Name
              </label>
              <input
                v-model="coiConfig.contact_name"
                type="text"
                class="form-control"
                placeholder="Michael Clements"
                :disabled="savingCoiConfig"
              >
            </div>
            <div class="form-group">
              <label class="form-label">
                <i class="fas fa-id-badge"></i>
                Contact Title
              </label>
              <input
                v-model="coiConfig.contact_title"
                type="text"
                class="form-control"
                placeholder="Purchasing Manager"
                :disabled="savingCoiConfig"
              >
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-at"></i>
              Contact Email
            </label>
            <input
              v-model="coiConfig.contact_email"
              type="email"
              class="form-control"
              placeholder="contact@company.com"
              :disabled="savingCoiConfig"
            >
            <span class="help-text">
              Used in the "If you have any questions, contact …" line of the email body.
              Leave blank to omit that line entirely.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-info-circle"></i>
              Special Instructions <span class="help-inline">(optional)</span>
            </label>
            <textarea
              v-model="coiConfig.special_instructions"
              class="form-control"
              rows="3"
              placeholder="If you are the Producer and do not issue Certificates of Insurance for your agency, please forward this email to the customer service representative responsible for issuing Certificates of Insurance for your clients."
              :disabled="savingCoiConfig"
            ></textarea>
            <span class="help-text">
              Appears as a "Special Instructions" block inside the email body.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-envelope"></i>
              Email Template
            </label>
            <select
              v-model="coiConfig.email_template_id"
              class="form-control"
              :disabled="savingCoiConfig || loadingTemplates"
            >
              <option :value="null">
                Platform default (COI Upload Request)
              </option>
              <option
                v-for="template in availableTemplates"
                :key="template.id"
                :value="template.id"
              >
                {{ template.name }}{{ template.owned_by_org ? '' : ' (platform default)' }}
              </option>
            </select>
            <span class="help-text">
              Pick a custom email template, or leave on "Platform default" to use the
              client-approved <code>coi-upload-request</code> template.
              <router-link to="/settings/email-branding" class="inline-link">
                Manage org templates →
              </router-link>
            </span>
          </div>

          <div class="form-actions">
            <button
              @click="saveCoiConfig"
              class="btn btn-primary"
              :disabled="savingCoiConfig">
              <i class="fas fa-save"></i>
              {{ savingCoiConfig ? 'Saving...' : 'Save COI Email Configuration' }}
            </button>
            <span v-if="coiConfigSavedMsg" class="config-save-msg" :class="coiConfigSavedMsgType">
              <i :class="coiConfigSavedMsgType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
              {{ coiConfigSavedMsg }}
            </span>
          </div>
        </div>
      </div>

      <!-- Project Check Settings Component -->
      <ProjectCheckSettings 
        ref="checkSettings"
        :project-id="Number(projectId)"
        @check-updated="handleCheckSettingsUpdated"
        @settings-updated="handleSettingsUpdated"
      />
      
      <!-- Client Requirements Customizer (shown when a check is selected) -->
      <ClientRequirementsCustomizer 
        v-if="selectedCheckDefinitionId"
        :check-definition-id="selectedCheckDefinitionId"
        :project-id="Number(projectId)"
        @saved="handleCustomizerSaved"
        @open-lab="navigateToLab"
      />
      
      <!-- Sample Documents Section -->
      <SampleDocuments 
        :project-id="Number(projectId)"
        @sample-uploaded="handleSampleUploaded"
        @sample-deleted="handleSampleDeleted"
      />
      
      <p class="settings-tools-note">
        Workspace tools (Check Lab, batch intake, documents) live on the
        <router-link :to="`/projects/${projectId}`">project home</router-link>.
      </p>

      <!-- Future settings sections can be added here -->
      <!-- 
      <div class="settings-section">
        <h3>Document Upload Settings</h3>
        <p>Configure automatic document processing and notification preferences.</p>
      </div>
      
      <div class="settings-section">
        <h3>Workflow Configuration</h3>
        <p>Set up automated workflows and approval processes.</p>
      </div>
      -->
    </div>
  </div>
</template>

<script>
import ProjectCheckSettings from '@/components/ProjectCheckSettings.vue';
import SampleDocuments from '@/components/SampleDocuments.vue';
import ClientRequirementsCustomizer from '@/components/ClientRequirementsCustomizer.vue';
// NOTE: BatchIntakeManager / BatchProcessingManager moved to ProjectBatchTools
// (project home → Tools). Settings is config-only.
import api from '@/services/api';

export default {
  name: 'ProjectSettings',
  components: {
    ProjectCheckSettings,
    SampleDocuments,
    ClientRequirementsCustomizer
  },
  props: {
    projectId: {
      type: [String, Number],
      default: null
    },
    clientId: {
      type: [String, Number], 
      default: null
    }
  },
  data() {
    return {
      projectName: '',
      clientName: '',
      loading: false,
      error: null,
      terminologySettings: {
        entity_terminology: 'companies',
        entity_term_singular: '',
        entity_term_plural: '',
        location_terminology: 'locations',
        location_term_custom: '',
        location_match_strategy: ''
      },
      savingTerminology: false,
      notificationSettings: {
        notify_on_upload_completion: false,
      },
      savingNotifications: false,
      statusReport: {
        enabled: false,
        interval: 'weekly',
        weekday: 0,
        recipientText: '',
        lastSentAt: null,
        showTest: false,
        testEmail: '',
      },
      weekdays: [
        { value: 0, label: 'Monday' },
        { value: 1, label: 'Tuesday' },
        { value: 2, label: 'Wednesday' },
        { value: 3, label: 'Thursday' },
        { value: 4, label: 'Friday' },
        { value: 5, label: 'Saturday' },
        { value: 6, label: 'Sunday' },
      ],
      savingStatusReport: false,
      sendingStatusPreview: false,
      sendingStatusNow: false,
      statusReportError: '',
      statusReportMessage: '',
      selectedCheckDefinitionId: null,
      // COI Upload Request Email config (ProjectUploadRequestConfig).
      // Lifted out of UploadRequestForm.vue so the Send modal is purely a
      // send action and admins have a single place to maintain the
      // certificate-holder block + project contact + template override.
      coiConfig: {
        certificate_holder_name: '',
        certificate_holder_address: '',
        contact_name: '',
        contact_title: '',
        contact_email: '',
        special_instructions: '',
        email_template_id: null,
      },
      savingCoiConfig: false,
      coiConfigSavedMsg: '',
      coiConfigSavedMsgType: 'success',
      availableTemplates: [],
      loadingTemplates: false,
    }
  },
  computed: {
    computedProjectId() {
      return this.projectId || this.$route.params.projectId;
    },
    computedClientId() {
      return this.clientId || this.$route.params.clientId;
    },
    previewEntitySingular() {
      if (this.terminologySettings.entity_terminology === 'custom') {
        return this.terminologySettings.entity_term_singular || 'Entity';
      }
      const terms = {
        companies: 'Company',
        subcontractors: 'Subcontractor',
        vendors: 'Vendor',
        franchisees: 'Franchisee',
        law_firms: 'Law Firm',
        suppliers: 'Supplier',
        partners: 'Partner',
        patients: 'Patient',
      };
      return terms[this.terminologySettings.entity_terminology] || 'Company';
    },
    previewEntityPlural() {
      if (this.terminologySettings.entity_terminology === 'custom') {
        return this.terminologySettings.entity_term_plural || 'Entities';
      }
      const terms = {
        companies: 'Companies',
        subcontractors: 'Subcontractors',
        vendors: 'Vendors',
        franchisees: 'Franchisees',
        law_firms: 'Law Firms',
        suppliers: 'Suppliers',
        partners: 'Partners',
        patients: 'Patients',
      };
      return terms[this.terminologySettings.entity_terminology] || 'Companies';
    },
    previewLocationPlural() {
      if (this.terminologySettings.location_terminology === 'custom') {
        return this.terminologySettings.location_term_custom ? `${this.terminologySettings.location_term_custom}s` : 'Locations';
      }
      const terms = {
        locations: 'Locations',
        stores: 'Stores',
        sites: 'Sites',
        jobsites: 'Job sites',
        houses: 'Houses',
        buildings: 'Buildings',
        offices: 'Offices',
        centers: 'Centers',
        branches: 'Branches'
      };
      return terms[this.terminologySettings.location_terminology] || 'Locations';
    },
    isTerminologyValid() {
      if (this.terminologySettings.entity_terminology === 'custom') {
        return this.terminologySettings.entity_term_singular && this.terminologySettings.entity_term_plural;
      }
      if (this.terminologySettings.location_terminology === 'custom') {
        return this.terminologySettings.location_term_custom;
      }
      return true;
    },
    companyCount() {
      return 15; // Placeholder for preview
    },
    statusReportLastSentLabel() {
      if (!this.statusReport.lastSentAt) return '';
      return new Date(this.statusReport.lastSentAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  },
  async mounted() {
    await this.loadProjectInfo();
    // Load COI config + available templates in parallel — neither blocks the
    // rest of the page if it fails (degrade to defaults).
    await Promise.allSettled([
      this.loadCoiConfig(),
      this.loadAvailableTemplates(),
      this.loadStatusReport(),
    ]);
  },
  methods: {
    async loadProjectInfo() {
      if (!this.computedProjectId) return;
      
      try {
        this.loading = true;
        const response = await api.get(`/projects/${this.computedProjectId}/`);
        const project = response.data.project || response.data;
        
        this.projectName = project.name;
        this.clientName = project.client_name || project.client?.name;
        
        // Load terminology settings
        this.terminologySettings = {
          entity_terminology: project.entity_terminology || 'companies',
          entity_term_singular: project.entity_term_singular || '',
          entity_term_plural: project.entity_term_plural || '',
          location_terminology: project.location_terminology || 'locations',
          location_term_custom: project.location_term_custom || '',
          location_match_strategy: project.location_match_strategy || ''
        };

        // Load notification settings (PR2)
        this.notificationSettings = {
          notify_on_upload_completion: !!project.notify_on_upload_completion,
        };

        // Load selected check definition ID if available
        if (project.default_comprehensive_check) {
          this.selectedCheckDefinitionId = project.default_comprehensive_check.id || project.default_comprehensive_check;
        }
      } catch (error) {
        console.error('Error loading project info:', error);
        this.error = 'Failed to load project information';
      } finally {
        this.loading = false;
      }
    },
    
    async saveTerminology() {
      if (!this.isTerminologyValid) return;
      
      try {
        this.savingTerminology = true;
        const response = await api.patch(`/projects/${this.computedProjectId}/`, this.terminologySettings);
        
        // Update local state with response data if available
        if (response.data) {
          const projectData = response.data.project || response.data;
          if (projectData.entity_terminology) {
            this.terminologySettings.entity_terminology = projectData.entity_terminology;
          }
          if (projectData.location_terminology) {
            this.terminologySettings.location_terminology = projectData.location_terminology;
          }
        }
        
        // Show success message (assuming you have a toast/notification system)
        if (this.$toast) {
          this.$toast.success('Terminology settings saved successfully!');
        } else {
          alert('Terminology settings saved!');
        }
      } catch (error) {
        console.error('Error saving terminology:', error);
        console.error('Error details:', error.response?.data);
        if (this.$toast) {
          this.$toast.error('Failed to save terminology settings');
        } else {
          alert('Failed to save terminology settings: ' + (error.response?.data?.error || error.message));
        }
      } finally {
        this.savingTerminology = false;
      }
    },
    
    async saveNotificationSettings() {
      try {
        this.savingNotifications = true;
        await api.patch(`/projects/${this.computedProjectId}/`, {
          notify_on_upload_completion: this.notificationSettings.notify_on_upload_completion,
        });
        if (this.$toast) {
          this.$toast.success('Notification settings saved.');
        } else {
          alert('Notification settings saved.');
        }
      } catch (error) {
        console.error('Error saving notification settings:', error);
        const msg = error.response?.data?.error || error.message;
        if (this.$toast) {
          this.$toast.error(`Failed to save notification settings: ${msg}`);
        } else {
          alert(`Failed to save notification settings: ${msg}`);
        }
      } finally {
        this.savingNotifications = false;
      }
    },

    async loadStatusReport() {
      if (!this.computedProjectId) return;
      try {
        const response = await api.get(`/projects/${this.computedProjectId}/status-report/`);
        const data = response.data || {};
        this.statusReport = {
          enabled: !!data.enabled,
          interval: data.interval || 'weekly',
          weekday: Number.isInteger(data.weekday) ? data.weekday : 0,
          recipientText: (data.recipient_emails || []).join('\n'),
          lastSentAt: data.last_sent_at || null,
          showTest: false,
          testEmail: '',
        };
      } catch (error) {
        console.error('Error loading status email settings:', error);
      }
    },

    async saveStatusReport() {
      this.statusReportError = '';
      this.statusReportMessage = '';
      this.savingStatusReport = true;
      try {
        const response = await api.patch(`/projects/${this.computedProjectId}/status-report/`, {
          enabled: this.statusReport.enabled,
          interval: this.statusReport.interval,
          weekday: this.statusReport.weekday,
          recipient_emails: this.statusReport.recipientText,
        });
        const data = response.data || {};
        this.statusReport.lastSentAt = data.last_sent_at || this.statusReport.lastSentAt;
        this.statusReport.recipientText = (data.recipient_emails || []).join('\n');
        this.statusReportMessage = 'Status email settings saved.';
      } catch (error) {
        const rejected = error.response?.data?.rejected;
        this.statusReportError = rejected && rejected.length
          ? `Check these addresses: ${rejected.join(', ')}`
          : (error.response?.data?.error || 'Could not save the status email.');
      } finally {
        this.savingStatusReport = false;
      }
    },

    async previewStatusReport() {
      const address = (this.statusReport.testEmail || '').trim();
      this.statusReportError = '';
      this.statusReportMessage = '';
      if (!address) {
        this.statusReportError = 'Type the address that should receive this test.';
        return;
      }
      this.sendingStatusPreview = true;
      try {
        const response = await api.post(
          `/projects/${this.computedProjectId}/status-report/preview/`,
          { to: address },
        );
        const sentTo = (response.data && response.data.sent_to) || [];
        this.statusReportMessage = sentTo.length
          ? `Test sent to ${sentTo.join(', ')}.`
          : 'Test sent.';
      } catch (error) {
        this.statusReportError = error.response?.data?.error || 'Could not send the test.';
      } finally {
        this.sendingStatusPreview = false;
      }
    },

    async sendStatusReportNow() {
      this.statusReportError = '';
      this.statusReportMessage = '';
      this.sendingStatusNow = true;
      try {
        const response = await api.post(`/projects/${this.computedProjectId}/status-report/send/`);
        const sentTo = (response.data && response.data.recipients) || [];
        this.statusReportMessage = sentTo.length
          ? `Status email sent to ${sentTo.join(', ')}.`
          : 'Status email sent.';
        this.statusReport.lastSentAt = new Date().toISOString();
      } catch (error) {
        this.statusReportError = error.response?.data?.error || 'Could not send the status email.';
      } finally {
        this.sendingStatusNow = false;
      }
    },

    async loadCoiConfig() {
      try {
        const response = await api.get(
          `/projects/${this.computedProjectId}/upload-request-config/`,
        );
        if (response.data) {
          this.coiConfig = {
            certificate_holder_name: response.data.certificate_holder_name || '',
            certificate_holder_address: response.data.certificate_holder_address || '',
            contact_name: response.data.contact_name || '',
            contact_title: response.data.contact_title || '',
            contact_email: response.data.contact_email || '',
            special_instructions: response.data.special_instructions || '',
            email_template_id: response.data.email_template_id || null,
          };
        }
      } catch (err) {
        console.error('Failed to load COI config:', err);
      }
    },

    async loadAvailableTemplates() {
      this.loadingTemplates = true;
      try {
        const response = await api.get(
          `/projects/${this.computedProjectId}/available-email-templates/`,
        );
        this.availableTemplates = response.data?.results || [];
      } catch (err) {
        console.error('Failed to load available email templates:', err);
        this.availableTemplates = [];
      } finally {
        this.loadingTemplates = false;
      }
    },

    async saveCoiConfig() {
      this.savingCoiConfig = true;
      this.coiConfigSavedMsg = '';
      try {
        await api.put(
          `/projects/${this.computedProjectId}/upload-request-config/`,
          this.coiConfig,
        );
        this.coiConfigSavedMsg =
          'Saved — applies to every upload-request email sent from this project.';
        this.coiConfigSavedMsgType = 'success';
        setTimeout(() => { this.coiConfigSavedMsg = ''; }, 5000);
      } catch (err) {
        console.error('Failed to save COI config:', err);
        const msg = err.response?.data?.error || err.message || 'Unknown error';
        this.coiConfigSavedMsg = `Failed to save: ${msg}`;
        this.coiConfigSavedMsgType = 'error';
      } finally {
        this.savingCoiConfig = false;
      }
    },

    onEntityTermChange() {
      // Clear custom fields if switching away from custom
      if (this.terminologySettings.entity_terminology !== 'custom') {
        this.terminologySettings.entity_term_singular = '';
        this.terminologySettings.entity_term_plural = '';
      }
    },
    
    onLocationTermChange() {
      // Clear custom field if switching away from custom
      if (this.terminologySettings.location_terminology !== 'custom') {
        this.terminologySettings.location_term_custom = '';
      }
    },
    
    handleCheckSettingsUpdated(event) {
      // Handle updates from the ProjectCheckSettings component
      console.log('Check settings updated:', event);
      // Could show a toast notification here
    },
    
    handleSettingsUpdated(event) {
      // Handle settings update from ProjectCheckSettings
      console.log('Settings updated:', event);
      if (event.selectedCheck) {
        this.selectedCheckDefinitionId = event.selectedCheck.id;
      } else {
        this.selectedCheckDefinitionId = null;
      }
    },
    
    handleCustomizerSaved(event) {
      // Handle save from ClientRequirementsCustomizer
      console.log('Customizer saved:', event);
      // Could show a toast notification here
    },
    
    navigateToLab(requirement) {
      // Navigate to RequirementLab with the selected requirement
      this.$router.push({
        name: 'RequirementLab',
        params: { projectId: this.computedProjectId },
        query: { requirementId: requirement.id }
      });
    },
    
    handleSampleUploaded(sampleDocument) {
      // Handle sample document upload
      console.log('Sample document uploaded:', sampleDocument);
      // Could show a success notification or update other components
    },
    
    handleSampleDeleted(sampleDocument) {
      // Handle sample document deletion
      console.log('Sample document deleted:', sampleDocument);
      // Could show a notification or update related state
    }
  }
};
</script>

<style scoped>
/* Project Settings Page Styling */
.project-settings-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
}

.breadcrumb-item {
  color: #667eea;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-item:hover {
  color: #5a67d8;
  text-decoration: none;
}

.breadcrumb-item.active {
  color: #718096;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 0.75rem;
  color: #cbd5e0;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
}

.header-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  backdrop-filter: blur(10px);
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: white;
}

.page-subtitle {
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
  line-height: 1.5;
}

.header-actions .btn {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.header-actions .btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  color: white;
}

/* Settings Content */
.settings-content {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.settings-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

.settings-tools-note {
  margin: 1.5rem 0 0;
  padding: 0.85rem 1rem;
  font-size: 0.9rem;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
}

.settings-tools-note a {
  color: var(--p-accent, #1e3a5f);
  font-weight: 600;
}

.settings-section h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.settings-section p {
  color: #718096;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .project-settings-page {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    text-align: center;
  }
  
  .header-content {
    flex-direction: column;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
}

/* Terminology Settings */
.terminology-form {
  padding: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #2d3748;
}

.form-group label i {
  margin-right: 0.5rem;
  color: #667eea;
}

.help-text {
  display: block;
  font-size: 0.875rem;
  color: #718096;
  margin-top: 0.5rem;
  font-style: italic;
}

.terminology-preview {
  background: #f7fafc;
  border: 2px dashed #cbd5e0;
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
}

.terminology-preview h4 {
  color: #2d3748;
  font-size: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.terminology-preview h4 i {
  color: #667eea;
}

.preview-examples {
  color: #4a5568;
}

.preview-examples p {
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.preview-examples ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.preview-examples li {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.preview-examples li:last-child {
  border-bottom: none;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

.status-report-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.status-report-test {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.status-report-test-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.status-report-test-row .form-control {
  flex: 1 1 16rem;
  min-width: 12rem;
}

.status-report-error {
  color: #c53030;
  margin: 0.5rem 0;
}

.status-report-ok {
  color: #276749;
  margin: 0.5rem 0;
}

/* Notification Settings (PR2) */
.notification-form {
  padding: 1.5rem;
}

.notification-form .toggle-group {
  margin-bottom: 1rem;
}

.notification-form .toggle-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f7fafc;
  transition: background 0.15s;
}

.notification-form .toggle-label:hover {
  background: #edf2f7;
}

.notification-form .toggle-label input[type="checkbox"] {
  margin-top: 0.25rem;
  width: 1.1rem;
  height: 1.1rem;
  flex-shrink: 0;
}

.notification-form .toggle-slider {
  display: none;
}

.notification-form .toggle-text {
  display: block;
  color: #2d3748;
}

.notification-form .toggle-text strong {
  display: block;
  margin-bottom: 0.25rem;
}

.notification-form .toggle-text small {
  display: block;
  color: #718096;
  font-size: 0.875rem;
  line-height: 1.4;
}

.notification-help {
  background: #ebf8ff;
  border-left: 3px solid #3182ce;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #2c5282;
  margin: 1rem 0;
}

.notification-help i {
  margin-right: 0.5rem;
}

/* COI Upload Request Email Configuration (PR B) */
.coi-config-form {
  padding: 1.5rem;
  background: #fafbff;
  border: 1px solid #e0e4ff;
  border-radius: 10px;
}

.coi-config-form .form-group {
  margin-bottom: 1.25rem;
}

.coi-config-form .form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
}

.coi-config-form .form-row .form-group {
  margin-bottom: 0;
}

.coi-config-form .form-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2d3748;
  font-size: 0.875rem;
}

.coi-config-form .form-label i {
  margin-right: 0.4rem;
  color: #667eea;
}

.coi-config-form code {
  background: #f1f5f9;
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
  font-size: 0.85em;
  color: #4338ca;
}

.coi-config-form .help-inline {
  font-weight: 400;
  font-size: 0.85em;
  color: #718096;
  margin-left: 0.4rem;
}

.coi-config-form .inline-link {
  color: #667eea;
  text-decoration: none;
  margin-left: 0.4rem;
}

.coi-config-form .inline-link:hover {
  text-decoration: underline;
}

.coi-config-form .form-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.config-save-msg {
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.config-save-msg.success {
  color: #2f855a;
}

.config-save-msg.error {
  color: #c53030;
}

@media (max-width: 768px) {
  .coi-config-form .form-row {
    grid-template-columns: 1fr;
  }
}
</style>